import type { PrimativeFieldPropsOmit } from '../../lib/utils';
import type { GradientPickerCopyProps, GradientPickerPasteProps, GradientPickerProps, GradientPickerTriggerProps, GradientPickerValueType } from '../form/gradient-picker';

import { useFieldContext } from '../../hooks/use-form-context';
import { GradientPicker } from '../form/gradient-picker';

export type GradientPickerFieldProps<T extends GradientPickerValueType> = Omit<GradientPickerProps<T>, PrimativeFieldPropsOmit> & {};

export function GradientPickerField<T extends GradientPickerValueType>(props: GradientPickerFieldProps<T>) {
    const field = useFieldContext<T>();

    return (
        <GradientPicker
            id={field.name}
            name={field.name}
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            {...props}
        />
    );
}

export type GradientPickerFieldTriggerProps = Omit<GradientPickerTriggerProps, 'gradient'>;

export function GradientPickerFieldTrigger(props: GradientPickerFieldTriggerProps) {
    const field = useFieldContext<GradientPickerValueType>();

    return (
        <GradientPicker.Trigger
            {...props}
            gradient={field.state.value || undefined}
        />
    );
}

GradientPickerField.Trigger = GradientPickerFieldTrigger;
GradientPickerFieldTrigger.displayName = 'GradientPickerFieldTrigger';

export type GradientPickerFieldCopyProps = Omit<GradientPickerCopyProps, 'gradient'>;

export function GradientPickerFieldCopy(props: GradientPickerFieldCopyProps) {
    const field = useFieldContext<GradientPickerValueType>();

    return (
        <GradientPicker.Copy
            {...props}
            gradient={field.state.value || undefined}
        />
    );
}

GradientPickerField.Copy = GradientPickerFieldCopy;
GradientPickerFieldCopy.displayName = 'GradientPickerFieldCopy';

export type GradientPickerFieldPasteProps<T extends GradientPickerValueType> = Omit<GradientPickerPasteProps<T>, 'onChange'>;

export function GradientPickerFieldPaste<T extends GradientPickerValueType>(props: GradientPickerFieldPasteProps<T>) {
    const { ...restProps } = props;

    const field = useFieldContext<T>();

    return (
        <GradientPicker.Paste
            {...restProps}
            onChange={field.handleChange as GradientPickerPasteProps<T>['onChange']}
        />
    );
}

GradientPickerField.Paste = GradientPickerFieldPaste;
GradientPickerFieldPaste.displayName = 'GradientPickerFieldPaste';
